import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const badgeVariants = cva(
  "inline-flex items-center rounded-full text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Default glassmorphism badge
        default: "badge-label badge-label--neutral",

        // Status variants
        active: "badge-label badge-label--active",
        success: "badge-label badge-label--success",
        paused: "badge-label badge-label--paused",
        stopped: "badge-label badge-label--stopped",
        error: "badge-label badge-label--error",
        warning: "badge-label badge-label--warning",
        lantern: "badge-label badge-label--lantern",
        inactive: "badge-label badge-label--inactive",

        // Connection variants
        connected: "badge-label badge-label--connected",
        "not-connected": "badge-label badge-label--not-connected",
        "in-progress": "badge-label badge-label--in-progress",
        reconnect: "badge-label badge-label--reconnect",

        // Billing variants
        paid: "badge-label badge-label--paid",
        unpaid: "badge-label badge-label--unpaid",

        // Generic variants
        neutral: "badge-label badge-label--neutral",
        info: "badge-label badge-label--info",
        price: "badge-label badge-label--price",
        primary: "badge-label badge-label--primary",
        accent: "badge-label badge-label--accent",

        // Platform status (header badges)
        platform: "badge-platform",

        // Legacy variants for backward compatibility
        secondary: "badge-label badge-label--neutral",
        destructive: "badge-label badge-label--error",
        outline: "badge-label badge-label--info",

        // Assistant type variants (AI/AUTO technology indicators)
        "type-ai": "badge-type badge-type--ai",
        "type-auto": "badge-type badge-type--auto",

        // Sub-assistant variants
        "sub": "badge-label badge-label--sub",
        "hybrid": "badge-label badge-label--hybrid",
        "coming-soon": "badge-label badge-label--coming-soon",
        "popular": "badge-label badge-label--popular",
        "template": "badge-type badge-type--template",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
